import React, { useState, useEffect, useCallback } from 'react'
import { Routes, Route, Navigate, useNavigate, useParams, useLocation, Outlet } from 'react-router-dom'
import { AuthProvider } from './auth/AuthProvider.jsx'
import { useAuth } from './auth/useAuth.js'
import { LoginPage } from './auth/LoginPage.jsx'
import { CallbackHandler } from './auth/CallbackHandler.jsx'

import { Sidebar } from './components/Sidebar.jsx'
import { Canvas } from './components/Canvas.jsx'
import { EmptyState } from './components/EmptyState.jsx'
import { TeamFeed } from './components/TeamFeed.jsx'
import { DeletionPendingBanner } from './components/DeletionPendingBanner.jsx'
import { ErrorBanner } from './components/ErrorBanner.jsx'
import { SettingsPanel } from './components/SettingsPanel.jsx'
import { MfaEnforcementModal } from './components/MfaEnforcementModal.jsx'
import { isTotpEnabled } from './auth/totp.js'
import { useSessions } from './hooks/useSessions.js'
import { useTeam } from './hooks/useTeam.js'
import { useTeamFeed } from './hooks/useTeamFeed.js'
import { useRole } from './hooks/useRole.js'
import { useCanvas } from './hooks/useCanvas.js'
import { useInvitations } from './hooks/useInvitations.js'
import { appsyncQuery } from './api/appsync-client.js'
import { useSubscription } from './api/useSubscription.js'
import { GoalPage } from './goals/GoalPage.jsx'
import { GoalForm } from './goals/GoalForm.jsx'
import { useGoals } from './goals/hooks/useGoals.js'
import { FlagsProvider } from './flags/FlagsProvider.jsx'
import { ReflectionsPage } from './reflections/ReflectionsPage.jsx'
import { ReflectionDetailPage } from './reflections/ReflectionDetailPage.jsx'
import { ReflectionForm } from './reflections/ReflectionForm.jsx'
import { useReflections } from './reflections/hooks/useReflections.js'
import { TasksPage } from './pages/TasksPage.jsx'
import { CreateTaskModal } from './components/tasks/CreateTaskModal.jsx'
import { DocsLayout } from './help/DocsLayout.jsx'
import { NotificationBannerStack } from './components/NotificationBannerStack.jsx'
import { reconcileAppearance, setRemoteSync } from './theme.js'
import { updateAppearance as apiUpdateAppearance } from './api/appearance.js'

setRemoteSync((payload) => {
  apiUpdateAppearance(payload).catch(() => { /* best-effort */ })
})
import { useFeedback } from './hooks/useFeedback.js'
import { FeedbackModal } from './components/feedback/FeedbackModal.jsx'
import { FeedbackTab } from './components/feedback/FeedbackTab.jsx'

const GET_ME = `
  query GetMe {
    getMe {
      userId email name org isManager isAdmin reportCount managerId managerName managerEmail
      orgMfaEnforced hasClaudeKey
      appearance { theme mode }
      deletionRequest { requestedAt scheduledFor status }
    }
  }
`

const REQUEST_DELETION = `
  mutation RequestDeletion {
    requestDeletion { requestedAt scheduledFor status }
  }
`

const CANCEL_DELETION = `
  mutation CancelDeletion {
    cancelDeletion
  }
`

const REMOVE_MY_MANAGER = `
  mutation RemoveMyManager {
    removeMyManager
  }
`

const ON_REPORT_SESSION_CHANGED = `
  subscription OnSessionListChanged($userId: ID!) {
    onSessionListChanged(userId: $userId) {
      sessionId userId managerId status energy scheduledAt
      createdAt updatedAt layout sections agenda
    }
  }
`

const UPDATE_REPORT_SESSION = `
  mutation UpdateReportSession($reportId: ID!, $sessionId: ID!, $input: UpdateSessionInput!) {
    updateReportSession(reportId: $reportId, sessionId: $sessionId, input: $input) {
      sessionId userId managerId status energy scheduledAt
      createdAt updatedAt layout sections agenda
    }
  }
`

// ─── Route-aware page components ──────────────────────────────────────

function PrepSessionPage({ sessions, me, selectedReportId, onUpdate, onSessionPatch, onReady, onCancel, onComplete, onNavigateGoals }) {
  const { sessionId } = useParams()
  const session = sessions.find((s) => s.sessionId === sessionId) || null

  if (!session) return null

  return (
    <Canvas
      session={session}
      onUpdate={onUpdate}
      onSessionPatch={onSessionPatch}
      onReady={onReady}
      onCancel={onCancel}
      onComplete={onComplete}
      readOnly={false}
      viewingReportName={null}
      managerName={me?.managerName ?? null}
      reportId={null}
      ownerUserId={me?.userId}
      onNavigateGoals={onNavigateGoals}
      currentUserId={me?.userId}
    />
  )
}

function PrepEmptyOrRedirect({ sessions, sessionsLoading, onNewSession }) {
  const navigate = useNavigate()

  useEffect(() => {
    if (sessions.length > 0) {
      navigate(`/prep/sessions/${sessions[0].sessionId}`, { replace: true })
    }
  }, [sessions, navigate])

  if (sessionsLoading || sessions.length > 0) return null
  return <EmptyState onCreateSession={onNewSession} />
}

function TeamSessionPage({ reportSessions, reports, selectedReportId, me, onUpdate, onSessionPatch, onCancel, onComplete, onNavigateGoals }) {
  const { reportId, sessionId } = useParams()
  const session = reportSessions.find((s) => s.sessionId === sessionId) || null
  const reportName = reports.find((r) => r.reportId === reportId)?.reportName || null

  const handleCancel = useCallback((id) => onCancel(id, reportId), [onCancel, reportId])
  const handleComplete = useCallback((id) => onComplete(id, reportId), [onComplete, reportId])

  if (!session) return null

  return (
    <Canvas
      session={session}
      onUpdate={onUpdate}
      onSessionPatch={onSessionPatch}
      onCancel={handleCancel}
      onComplete={handleComplete}
      readOnly={session.status === 'completed' || session.status === 'cancelled'}
      viewingReportName={reportName}
      managerName={null}
      reportId={reportId}
      ownerUserId={reportId}
      onNavigateGoals={onNavigateGoals}
      currentUserId={me?.userId}
    />
  )
}

function TeamReportPage({ reportSessions, reports, selectedReportId, reportSessionsLoading, me, onUpdate, onSessionPatch, onReady, onCancel, onComplete, onNavigateGoals }) {
  const { reportId } = useParams()
  const navigate = useNavigate()

  // Auto-select first session when report is selected
  useEffect(() => {
    if (!reportSessionsLoading && reportSessions.length > 0 && reportId === selectedReportId) {
      navigate(`/team/reports/${reportId}/sessions/${reportSessions[0].sessionId}`, { replace: true })
    }
  }, [reportSessions, reportSessionsLoading, reportId, selectedReportId, navigate])

  return null
}

function TeamGoalPage({ me, reports, reloadGoals }) {
  const { reportId, goalId } = useParams()
  const navigate = useNavigate()

  return (
    <GoalPage
      goalId={goalId}
      me={me}
      ownerUserId={reportId}
      onBack={() => navigate(`/team/reports/${reportId}`)}
      onGoalChanged={reloadGoals}
    />
  )
}

function PrepGoalPage({ me, reloadGoals }) {
  const { goalId } = useParams()
  const navigate = useNavigate()

  return (
    <GoalPage
      goalId={goalId}
      me={me}
      ownerUserId={null}
      onBack={() => navigate('/prep')}
      onGoalChanged={reloadGoals}
    />
  )
}

function PrepReflectionPage({ me, reloadReflections }) {
  const { reflectionId } = useParams()
  const navigate = useNavigate()

  return (
    <ReflectionDetailPage
      reflectionId={reflectionId}
      me={me}
      ownerUserId={null}
      onBack={() => navigate('/reflections')}
      onReflectionChanged={reloadReflections}
    />
  )
}

function TeamReflectionPage({ me, reports, reloadReflections }) {
  const { reportId, reflectionId } = useParams()
  const navigate = useNavigate()

  return (
    <ReflectionDetailPage
      reflectionId={reflectionId}
      me={me}
      ownerUserId={reportId}
      onBack={() => navigate(`/team/reports/${reportId}/reflections`)}
      onReflectionChanged={reloadReflections}
      onNavigateGoal={(goalId) => navigate(`/team/reports/${reportId}/goals/${goalId}`)}
    />
  )
}

function SettingsRoute({ user, me, reports, onAddReport, onRemoveReport, onRemoveManager, onUpdateReportTags, canvasLayout, onSaveCanvas, onRequestDeletion, onCancelDeletion }) {
  const navigate = useNavigate()

  return (
    <SettingsPanel
      user={user}
      me={me}
      reports={reports}
      onAddReport={onAddReport}
      onRemoveReport={onRemoveReport}
      onRemoveManager={onRemoveManager}
      onUpdateReportTags={onUpdateReportTags}
      onClose={() => navigate(-1)}
      canvasLayout={canvasLayout}
      onSaveCanvas={onSaveCanvas}
      onRequestDeletion={onRequestDeletion}
      onCancelDeletion={onCancelDeletion}
    />
  )
}

function DocsRoute() {
  const { page } = useParams()
  const navigate = useNavigate()

  return (
    <DocsLayout
      page={page || 'releases'}
      onNavigate={(p) => navigate(`/docs/${p}`)}
    />
  )
}

function TasksRoute({ me, onCreateClicked }) {
  return (
    <TasksPage
      me={me}
      currentUserId={me?.userId}
      onCreateClicked={onCreateClicked}
    />
  )
}

// ─── Main app shell ───────────────────────────────────────────────────

function AppContent() {
  const { user, loading: authLoading, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [me, setMe] = useState(null)
  const [deactivated, setDeactivated] = useState(false)
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false)
  const [mfaModalOpen, setMfaModalOpen] = useState(false)

  const { role, setRole, isExplicit } = useRole()
  const authenticated = !authLoading && !!user
  // While the enforced-MFA modal is open, every non-getMe API call would
  // be rejected with MFA_REQUIRED. Disable the data-fetching hooks so the
  // main area doesn't spam errors behind the modal.
  const apiEnabled = authenticated && !mfaModalOpen

  const {
    sessions, loading: sessionsLoading, error: sessionsError, createSession, updateSession, deleteSession, readySession, cancelSession, completeSession, patchSession,
  } = useSessions({ enabled: apiEnabled, userId: me?.userId })
  const {
    reports, loading: reportsLoading, error: teamError, loadReportSessions, addReport, updateReportTags, removeReport,
  } = useTeam({ enabled: apiEnabled })
  const { layout: canvasLayout, error: canvasError, saveCanvas } = useCanvas({ enabled: apiEnabled })

  // Derive selectedReportId and selectedSessionId from URL
  const selectedReportId = (() => {
    const match = location.pathname.match(/^\/team\/reports\/([^/]+)/)
    return match ? match[1] : null
  })()

  const selectedSessionId = (() => {
    const match = location.pathname.match(/\/sessions\/([^/]+)/)
    return match ? match[1] : null
  })()

  const [reportSessions, setReportSessions] = useState([])
  const { feedbackOpen, openFeedbackModal, closeFeedbackModal, submitFeedback } = useFeedback(me, role, selectedSessionId)

  const { feedSessions, loading: feedLoading } = useTeamFeed({ enabled: apiEnabled && role === 'team' && !selectedReportId })

  const { goals, createGoal, deleteGoal: deleteGoalFromSidebar, reload: reloadGoals } = useGoals({
    ownerUserId: role === 'team' && selectedReportId ? selectedReportId : null,
    enabled: apiEnabled && (role === 'prep' || (role === 'team' && !!selectedReportId)),
  })

  const { reflections, createReflection: createReflectionFromSidebar, deleteReflection: deleteReflectionFromSidebar, reload: reloadReflections } = useReflections({
    ownerUserId: role === 'team' && selectedReportId ? selectedReportId : null,
    enabled: apiEnabled && (role === 'prep' || (role === 'team' && !!selectedReportId)),
  })

  const [dismissedError, setDismissedError] = useState(null)
  const activeError = [sessionsError, teamError, canvasError].find(Boolean) || null
  const visibleError = activeError !== dismissedError ? activeError : null
  const { invitations, approve: approveInvitation, decline: declineInvitation } = useInvitations({ enabled: apiEnabled })
  const [reportSessionsLoading, setReportSessionsLoading] = useState(false)

  // Subscribe to report's session changes when manager is viewing a report
  useSubscription(
    ON_REPORT_SESSION_CHANGED,
    { userId: selectedReportId },
    (data) => {
      const updated = data.onSessionListChanged
      if (!updated) return
      if (typeof updated.sections === 'string') try { updated.sections = JSON.parse(updated.sections) } catch { /* keep */ }
      if (typeof updated.agenda === 'string') try { updated.agenda = JSON.parse(updated.agenda) } catch { /* keep */ }
      setReportSessions((prev) => {
        const exists = prev.some((s) => s.sessionId === updated.sessionId)
        if (exists) return prev.map((s) => (s.sessionId === updated.sessionId ? updated : s))
        return [updated, ...prev]
      })
    },
    { enabled: role === 'team' && !!selectedReportId },
  )

  useEffect(() => {
    if (user) {
      appsyncQuery(GET_ME).then((data) => {
        const me = data.getMe
        setMe(me)
        if (me?.appearance) {
          reconcileAppearance(me.appearance)
        }
        if (me?.isManager && !isExplicit) {
          setRole('team')
        }
        if (me?.orgMfaEnforced) {
          isTotpEnabled(user.accessToken)
            .then((enabled) => setMfaModalOpen(!enabled))
            .catch(() => setMfaModalOpen(true))
        } else {
          setMfaModalOpen(false)
        }
      }).catch((err) => {
        if (err?.code === 'ACCOUNT_DEACTIVATED') {
          setDeactivated(true)
          signOut()
        }
      })
    }
  }, [user])

  // Load report sessions when navigating to a report
  const lastLoadedReportRef = React.useRef(null)
  useEffect(() => {
    if (!authenticated) return
    if (selectedReportId && selectedReportId !== lastLoadedReportRef.current) {
      lastLoadedReportRef.current = selectedReportId
      setReportSessionsLoading(true)
      loadReportSessions(selectedReportId)
        .then((data) => {
          setReportSessions(data.sessions || [])
        })
        .catch(() => setReportSessions([]))
        .finally(() => setReportSessionsLoading(false))
    }
    if (!selectedReportId) {
      lastLoadedReportRef.current = null
    }
  }, [authenticated, selectedReportId, loadReportSessions])

  // Sync role from URL path
  useEffect(() => {
    if (location.pathname.startsWith('/prep') || location.pathname.startsWith('/goals') || location.pathname.startsWith('/reflections')) {
      if (role !== 'prep') setRole('prep')
    } else if (location.pathname.startsWith('/team')) {
      if (role !== 'team') setRole('team')
    }
  }, [location.pathname])

  const handleNewSession = useCallback(async () => {
    const s = await createSession({ managerId: me?.managerId })
    navigate(`/prep/sessions/${s.sessionId}`)
  }, [createSession, navigate, user])

  const handleUpdate = useCallback(async (id, data, opts = {}) => {
    if (opts.silent) {
      setReportSessions((prev) => prev.map((s) => (s.sessionId === id ? { ...s, ...data } : s)))
      return
    }
    if (selectedReportId) {
      const { energy, scheduledAt, sections, layout, agenda } = data
      const input = {}
      if (energy      !== undefined) input.energy      = energy
      if (scheduledAt !== undefined) input.scheduledAt = scheduledAt
      if (layout      !== undefined) input.layout      = layout
      if (sections    !== undefined) input.sections    = JSON.stringify(sections)
      if (agenda      !== undefined) input.agenda      = JSON.stringify(agenda)
      await appsyncQuery(UPDATE_REPORT_SESSION, { reportId: selectedReportId, sessionId: id, input })
    } else {
      await updateSession(id, data)
    }
  }, [selectedReportId, updateSession])

  const handleSessionPatch = useCallback((id, patch) => {
    patchSession(id, patch)
    setReportSessions((prev) => prev.map((s) => (s.sessionId === id ? { ...s, ...patch } : s)))
  }, [patchSession])

  const handleReady = useCallback(async (id) => {
    await readySession(id)
  }, [readySession])

  const handleCancel = useCallback(async (id, ownerUserId = null) => {
    await cancelSession(id, ownerUserId)
  }, [cancelSession])

  const handleComplete = useCallback(async (id, ownerUserId = null) => {
    await completeSession(id, ownerUserId)
  }, [completeSession])

  const handleAddReport = useCallback(async (email) => {
    await addReport(email)
    appsyncQuery(GET_ME).then((d) => setMe(d.getMe)).catch(() => {})
  }, [addReport])

  const handleRequestDeletion = useCallback(async () => {
    await appsyncQuery(REQUEST_DELETION)
    appsyncQuery(GET_ME).then((d) => setMe(d.getMe)).catch(() => {})
  }, [])

  const handleCancelDeletion = useCallback(async () => {
    await appsyncQuery(CANCEL_DELETION)
    appsyncQuery(GET_ME).then((d) => setMe(d.getMe)).catch(() => {})
  }, [])

  const [creatingGoal, setCreatingGoal] = useState(false)

  const handleNewGoal = useCallback(() => {
    setCreatingGoal(true)
  }, [])

  const handleCreateGoal = useCallback(async (body) => {
    const goal = await createGoal(body)
    setCreatingGoal(false)
    if (role === 'team' && selectedReportId) {
      navigate(`/team/reports/${selectedReportId}/goals/${goal.goalId}`)
    } else {
      navigate(`/goals/${goal.goalId}`)
    }
  }, [createGoal, navigate, role, selectedReportId])

  const handleSelectGoal = useCallback((goalId) => {
    if (role === 'team' && selectedReportId) {
      navigate(`/team/reports/${selectedReportId}/goals/${goalId}`)
    } else {
      navigate(`/goals/${goalId}`)
    }
  }, [navigate, role, selectedReportId])

  const [creatingReflection, setCreatingReflection] = useState(false)

  const handleNewReflection = useCallback(() => {
    setCreatingReflection(true)
  }, [])

  const handleCreateReflection = useCallback(async (body) => {
    const reflection = await createReflectionFromSidebar(body)
    setCreatingReflection(false)
    if (role === 'team' && selectedReportId) {
      navigate(`/team/reports/${selectedReportId}/reflections/${reflection.reflectionId}`)
    } else {
      navigate(`/reflections/${reflection.reflectionId}`)
    }
  }, [createReflectionFromSidebar, navigate, role, selectedReportId])

  const handleSelectReflection = useCallback((reflectionId) => {
    if (role === 'team' && selectedReportId) {
      navigate(`/team/reports/${selectedReportId}/reflections/${reflectionId}`)
    } else {
      navigate(`/reflections/${reflectionId}`)
    }
  }, [navigate, role, selectedReportId])

  const handleRemoveManager = useCallback(async () => {
    await appsyncQuery(REMOVE_MY_MANAGER)
    appsyncQuery(GET_ME).then((d) => setMe(d.getMe)).catch(() => {})
  }, [])

  const handleRoleChange = useCallback((r) => {
    setRole(r)
    if (r === 'prep') navigate('/prep')
    else if (r === 'team') navigate('/team')
  }, [setRole, navigate])

  // Handle OAuth2 callback before the auth gate — user isn't set yet
  if (location.pathname === '/callback') {
    return <CallbackHandler />
  }

  if (authLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading...</span>
      </div>
    )
  }

  if (!user) {
    if (deactivated) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
          <div style={{ textAlign: 'center', maxWidth: '320px' }}>
            <p style={{ fontSize: '15px', fontWeight: 600, marginBottom: '8px' }}>Account deactivated</p>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              Your account has been deactivated. Please contact your admin.
            </p>
          </div>
        </div>
      )
    }
    return <LoginPage />
  }

  // Org enforces MFA and user has no TOTP: show only the enrollment modal.
  // Rendering the full shell behind it leaves the main area stuck on loaders
  // (hooks gated off) or noisy with MFA_REQUIRED errors (if ungated).
  if (mfaModalOpen) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
        <MfaEnforcementModal
          user={user}
          onEnrolled={() => {
            setMfaModalOpen(false)
            appsyncQuery(GET_ME).then((d) => setMe(d.getMe)).catch(() => {})
          }}
        />
      </div>
    )
  }

  const isManager = me?.isManager || false
  const deletionPending = !!me?.deletionRequest

  // Derive active goal and review from URL
  const goalMatch = location.pathname.match(/\/goals\/([^/]+)/)
  const selectedGoalId = goalMatch ? goalMatch[1] : null
  const reflectionMatch = location.pathname.match(/\/reflections\/([^/]+)/)
  const selectedReflectionId = reflectionMatch ? reflectionMatch[1] : null

  return (
    <div style={appStyles.layout}>
      <Sidebar
        isManager={isManager}
        role={role}
        onRoleChange={handleRoleChange}
        sessions={sessions}
        selectedSessionId={selectedSessionId}
        onSelectSession={(id) => navigate(`/prep/sessions/${id}`)}
        onNewSession={handleNewSession}
        onDeleteSession={deleteSession}
        reports={reports}
        selectedReportId={selectedReportId}
        onSelectReport={(id) => navigate(`/team/reports/${id}`)}
        onAddReport={handleAddReport}
        onRemoveReport={removeReport}
        reportSessions={reportSessions}
        onSelectReportSession={selectedReportId ? (id) => navigate(`/team/reports/${selectedReportId}/sessions/${id}`) : undefined}
        onSignOut={signOut}
        settingsOpen={location.pathname === '/settings'}
        onOpenSettings={() => navigate('/settings')}
        goals={goals}
        selectedGoalId={selectedGoalId}
        onSelectGoal={handleSelectGoal}
        onNewGoal={handleNewGoal}
        onDeleteGoal={deleteGoalFromSidebar}
        invitations={invitations}
        onApproveInvitation={approveInvitation}
        onDeclineInvitation={declineInvitation}
        deletionPending={deletionPending}
        docsOpen={location.pathname.startsWith('/docs')}
        onOpenDocs={() => navigate('/docs/releases')}
        tasksOpen={location.pathname.startsWith('/tasks')}
        onOpenTasks={() => navigate('/tasks')}
        onNewTask={() => setCreateTaskModalOpen(true)}
        onOpenFeedback={openFeedbackModal}
        reflections={reflections}
        selectedReflectionId={selectedReflectionId}
        onSelectReflection={handleSelectReflection}
        onNewReflection={handleNewReflection}
        onDeleteReflection={deleteReflectionFromSidebar}
        me={me}
      />
      <main style={appStyles.main}>
        <ErrorBanner error={visibleError} onDismiss={() => setDismissedError(activeError)} />
        <DeletionPendingBanner deletionRequest={me?.deletionRequest} />
        <NotificationBannerStack me={me} enabled={apiEnabled && !!me} />
        <Routes>
          {/* Prep routes */}
          <Route path="/prep" element={
            <PrepEmptyOrRedirect sessions={sessions} sessionsLoading={sessionsLoading} onNewSession={handleNewSession} />
          } />
          <Route path="/prep/sessions/:sessionId" element={
            <PrepSessionPage
              sessions={sessions}
              me={me}
              selectedReportId={selectedReportId}
              onUpdate={handleUpdate}
              onSessionPatch={handleSessionPatch}
              onReady={handleReady}
              onCancel={handleCancel}
              onComplete={handleComplete}
              onNavigateGoals={() => navigate('/goals')}
            />
          } />

          {/* Prep goals */}
          <Route path="/goals/:goalId" element={
            <PrepGoalPage me={me} reloadGoals={reloadGoals} />
          } />

          {/* Prep reflections (feature-flagged) */}
          <Route path="/reflections" element={
            <ReflectionsPage me={me} onSelectReflection={(id) => navigate(`/reflections/${id}`)} />
          } />
          <Route path="/reflections/:reflectionId" element={
            <PrepReflectionPage me={me} />
          } />

          {/* Team routes */}
          <Route path="/team" element={
            <TeamFeed
              feedSessions={feedSessions}
              reports={reports}
              loading={feedLoading}
              onSelectReportSession={(reportId, sessionId) => {
                navigate(`/team/reports/${reportId}/sessions/${sessionId}`)
              }}
            />
          } />
          <Route path="/team/reports/:reportId" element={
            <TeamReportPage
              reportSessions={reportSessions}
              reports={reports}
              selectedReportId={selectedReportId}
              reportSessionsLoading={reportSessionsLoading}
              me={me}
              onUpdate={handleUpdate}
              onSessionPatch={handleSessionPatch}
              onReady={handleReady}
              onCancel={handleCancel}
              onComplete={handleComplete}
              onNavigateGoals={() => navigate('/goals')}
            />
          } />
          <Route path="/team/reports/:reportId/sessions/:sessionId" element={
            <TeamSessionPage
              reportSessions={reportSessions}
              reports={reports}
              selectedReportId={selectedReportId}
              me={me}
              onUpdate={handleUpdate}
              onSessionPatch={handleSessionPatch}
              onCancel={handleCancel}
              onComplete={handleComplete}
              onNavigateGoals={(reportId) => navigate(`/team/reports/${reportId}`)}
            />
          } />
          <Route path="/team/reports/:reportId/goals/:goalId" element={
            <TeamGoalPage me={me} reports={reports} reloadGoals={reloadGoals} />
          } />

          {/* Team reflections (feature-flagged) */}
          <Route path="/team/reports/:reportId/reflections" element={
            <ReflectionsPage
              me={me}
              ownerUserId={selectedReportId}
              onSelectReflection={(id) => navigate(`/team/reports/${selectedReportId}/reflections/${id}`)}
            />
          } />
          <Route path="/team/reports/:reportId/reflections/:reflectionId" element={
            <TeamReflectionPage me={me} reports={reports} />
          } />

          {/* Tasks */}
          <Route path="/tasks" element={
            <TasksRoute me={me} onCreateClicked={() => setCreateTaskModalOpen(true)} />
          } />

          {/* Settings */}
          <Route path="/settings" element={
            <SettingsRoute
              user={user}
              me={me}
              reports={reports}
              onAddReport={handleAddReport}
              onRemoveReport={removeReport}
              onRemoveManager={handleRemoveManager}
              onUpdateReportTags={updateReportTags}
              canvasLayout={canvasLayout}
              onSaveCanvas={saveCanvas}
              onRequestDeletion={handleRequestDeletion}
              onCancelDeletion={handleCancelDeletion}
            />
          } />

          {/* Docs */}
          <Route path="/docs/:page" element={<DocsRoute />} />
          <Route path="/docs" element={<Navigate to="/docs/releases" replace />} />

          {/* Default redirect */}
          <Route path="*" element={<DefaultRedirect role={role} />} />
        </Routes>
      </main>
      {creatingGoal && (
        <div style={appStyles.modalOverlay} onClick={() => setCreatingGoal(false)}>
          <div style={appStyles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={appStyles.modalTitle}>New goal</h3>
            <GoalForm
              ownerUserId={role === 'team' && selectedReportId ? selectedReportId : null}
              onSave={handleCreateGoal}
              onCancel={() => setCreatingGoal(false)}
            />
          </div>
        </div>
      )}
      {creatingReflection && (
        <div style={appStyles.modalOverlay} onClick={() => setCreatingReflection(false)}>
          <div style={appStyles.modalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={appStyles.modalTitle}>New reflection</h3>
            <ReflectionForm
              ownerUserId={role === 'team' && selectedReportId ? selectedReportId : null}
              existingTitles={reflections.filter((r) => r.status === 'open').map((r) => r.title)}
              onSave={handleCreateReflection}
              onCancel={() => setCreatingReflection(false)}
            />
          </div>
        </div>
      )}
      <CreateTaskModal
        open={createTaskModalOpen}
        onClose={() => setCreateTaskModalOpen(false)}
        me={me}
        reports={reports}
      />
      <FeedbackTab onClick={openFeedbackModal} />
      <FeedbackModal
        open={feedbackOpen}
        onClose={closeFeedbackModal}
        onSubmit={submitFeedback}
      />
    </div>
  )
}

function DefaultRedirect({ role }) {
  if (role === 'team') return <Navigate to="/team" replace />
  return <Navigate to="/prep" replace />
}

export default function App() {
  return (
    <AuthProvider>
      <FlagsProvider>
        <AppContent />
      </FlagsProvider>
    </AuthProvider>
  )
}

const appStyles = {
  layout: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
  },
  main: {
    flex: 1,
    overflowY: 'auto',
    scrollbarGutter: 'stable',
    background: 'var(--bg)',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '24px',
  },
  modalCard: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    padding: '24px',
    width: '100%',
    maxWidth: '520px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: 'var(--shadow-lg)',
  },
  modalTitle: {
    fontSize: '16px',
    fontWeight: 700,
    margin: '0 0 16px 0',
  },
}
